import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { MatIcon } from '@angular/material/icon';
import { SubscriptionLike } from 'rxjs';
import L, { LeafletKeyboardEvent } from 'leaflet';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { getAgTheme } from '@app/components/grid/ag-grid-theme';
import { AgSetFilterComponent } from '@app/components/grid/filters/ag-set-filter/ag-set-filter.component';
import { AgRouteRendererComponent } from '@app/components/grid/renderers/ag-route-renderer/ag-route-renderer.component';
import { DataService } from '@app/services/data.service';
import { StorageService } from '@app/services/storage.service';
import { MapInstanceService } from '@app/services/map-instance.service';
import { IMapInit } from '@app/services/map.service';
import { IArea, IWingedLight } from 'skygame-data';
import { SectionQuickActionsComponent } from '@app/components/shared/quick-actions/section-quick-actions.component';
import { QuickActionsComponent } from '@app/components/shared/quick-actions/quick-actions.component';
import { TooltipDirective } from '@app/directives/tooltip.directive';

interface IRow {
  area: IArea;
  wl: Array<IWingedLight>;
  unlocked: number;
}

interface IMapWingedLight {
  wl: IWingedLight;
  marker?: L.Marker;
  prev?: IMapWingedLight;
  next?: IMapWingedLight;
}

@Component({
  selector: 'app-children-of-light',
  templateUrl: './children-of-light.component.html',
  styleUrl: './children-of-light.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MapInstanceService],
  imports: [TooltipDirective, MatIcon, AgGridAngular, SectionQuickActionsComponent, QuickActionsComponent]
})
export class ChildrenOfLightComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;
  @ViewChild('table', { static: true }) table!: ElementRef;

  readonly theme = getAgTheme();
  rowData: any[] = [];
  api?: GridApi;

  readonly colDefs: ColDef[] = [
    {
      headerName: '',
      width: 60,
      sortable: false,
      filter: false,
      cellRenderer: (p: any) => {
        const row: IRow = p.data.row;
        return `<span class="atmos-col__map-icon" onclick="atmosColShowArea('${row.area.guid}')"><span class="maticon">location_on</span></span>`;
      }
    },
    {
      headerName: 'Found',
      width: 140,
      filter: 'agNumberColumnFilter',
      valueGetter: (p: ValueGetterParams) => p.data.row.unlocked,
      cellRenderer: (p: any) => {
        const row: IRow = p.data.row;
        const cls = row.unlocked === row.wl.length
          ? 'atmos-col__row-complete' : row.unlocked ? 'atmos-col__row-partial' : '';
        return `<span class="atmos-col__row-unlocked ${cls}">${row.unlocked} / ${row.wl.length}</span><span class="maticon atmos-col__row-toggle" onclick="atmosColToggleRow('${row.area.guid}')">lock_open</span>`;
      }
    },
    {
      field: 'area',
      headerName: 'Area',
      flex: 1,
      minWidth: 150,
      filter: 'agTextColumnFilter',
      cellRenderer: AgRouteRendererComponent,
      filterValueGetter: (p: ValueGetterParams) => p.data.area?.label ?? ''
    },
    {
      field: 'realm',
      headerName: 'Realm',
      flex: 1,
      minWidth: 150,
      filter: AgSetFilterComponent,
      filterParams: { values: [] as string[], includeBlanks: true },
      cellRenderer: AgRouteRendererComponent,
      filterValueGetter: (p: ValueGetterParams) => p.data.realm?.label ?? ''
    }
  ];

  unlockedCol = 0;
  totalCol = 0;

  private readonly wingedLights: Array<IWingedLight>;
  light: Array<IMapWingedLight> = [];
  lightMap: { [wlGuid: string]: IMapWingedLight } = {};
  rows: Array<IRow>;
  rowMap: { [wlGuid: string]: IRow } = {};

  shownArea?: IArea;

  map!: L.Map;
  private _lastPopup?: L.Popup;
  private _subWidth?: SubscriptionLike;

  constructor(
    private readonly _dataService: DataService,
    private readonly _storageService: StorageService,
    private readonly _mapInstanceService: MapInstanceService,
    private readonly _route: ActivatedRoute,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _breakpointObserver: BreakpointObserver,
    private readonly _zone: NgZone,
  ) {
    const realmColDef = this.colDefs.find(c => c.field === 'realm')!;
    realmColDef.filterParams.values = this._dataService.realmConfig.items.map(r => r.name);

    this.unlockedCol = _storageService.getWingedLights().size;
    this.totalCol = this._dataService.wingedLightConfig.items.length;

    this.wingedLights = [...this._dataService.wingedLightConfig.items].sort((a, b) => a.order - b.order);

    const areaSet = new Set<string>();
    this.rows = [];
    this.wingedLights.forEach(wl => {
      const area = wl.area;
      if (!area || areaSet.has(area.guid)) { return; }
      areaSet.add(area.guid);
      const row: IRow = {
        area,
        wl: area.wingedLights || [],
        unlocked: (area.wingedLights || []).filter(w => w.unlocked).length
      };
      row.wl.forEach(w => { this.rowMap[w.guid] = row; });
      this.rows.push(row);
    });

    this.rowData = this.rows.map(row => ({
      row,
      area: { label: row.area.name, route: ['/area', row.area.guid] },
      realm: row.area.realm ? { label: row.area.realm.name, route: ['/realm', row.area.realm.guid] } : {}
    }));
  }

  ngAfterViewInit(): void {
    const wlGuid = this._route.snapshot.queryParamMap.get('wl');
    const wl = wlGuid ? this._dataService.guidMap.get(wlGuid) as IWingedLight : undefined;
    const areaGuid = this._route.snapshot.queryParamMap.get('area');
    const area = areaGuid ? this._dataService.guidMap.get(areaGuid) as IArea : undefined;

    let mapInit: IMapInit;
    if (wl) {
      mapInit = { view: wl.mapData?.position, zoom: 3 };
    } else if (area?.mapData?.position) {
      mapInit = { view: area.mapData.position, zoom: 3 };
    } else {
      mapInit = { fromQuery: true };
    }
    this.map = this._mapInstanceService.initialize(this.mapContainer.nativeElement.querySelector('.atmos-col__map'), mapInit);

    const layerGroup = L.layerGroup().addTo(this.map);
    const wlIcon = L.icon({
      iconUrl: 'assets/icons/light.svg',
      iconSize: [32, 32],
      popupAnchor: [0, -12],
    });

    L.imageOverlay('assets/game/map/void.webp', [[-146.12, 173.38], [-121.13, 198.38]]).addTo(layerGroup);

    this.wingedLights.filter(w => w.mapData?.position).forEach(w => {
      if (!w.mapData) { return; }
      const obj: IMapWingedLight = { wl: w };

      obj.marker = L.marker(w.mapData.position!, {
        icon: wlIcon,
        opacity: w.unlocked ? 0.4 : 1,
      });

      const popup = new L.Popup({
        content: () => this.createPopup(w),
        minWidth: 480, maxWidth: 480
      });
      obj.marker.bindPopup(popup);

      layerGroup.addLayer(obj.marker);

      this.light.push(obj);
      this.lightMap[w.guid] = obj;
    });

    for (let i = 0; i < this.light.length; i++) {
      const obj = this.light[i];
      obj.prev = this.light[i - 1] || this.light[this.light.length - 1];
      obj.next = this.light[i + 1] || this.light[0];
    }

    if (wl) {
      setTimeout(() => { this.flyAndOpen(wl); });
    }

    (window as any).markCol = (elem: HTMLElement, guid: string, next: boolean) => {
      this._zone.run(() => { this.markCol(elem, guid, next); });
    };
    (window as any).nextCol = (guid: string, direction: number) => {
      this._zone.run(() => { direction < 0 ? this.prevCol(guid) : this.nextCol(guid); });
    };
    (window as any).atmosColShowArea = (g: string) => {
      this._zone.run(() => {
        const row = this.rows.find(r => r.area.guid === g);
        if (row) { this.showArea(row); }
      });
    };
    (window as any).atmosColToggleRow = (g: string) => {
      this._zone.run(() => {
        const row = this.rows.find(r => r.area.guid === g);
        if (row) {
          this.toggleRow(row);
          this.api?.refreshCells({ force: true });
        }
      });
    };

    this._mapInstanceService.on('popupopen', (e: L.PopupEvent) => { this.onPopupOpen(e); });
    this._mapInstanceService.on('moveend', () => { this.onMoveEnd(); });
    this._mapInstanceService.on('keydown', (e: LeafletKeyboardEvent) => { this.onKeydown(e); });

    this._subWidth = this._breakpointObserver.observe(['(min-width: 720px)']).subscribe(s => this.onResponsive(s));
  }

  onGridReady(evt: GridReadyEvent): void {
    this.api = evt.api;
  }

  onResponsive(s: BreakpointState): void {
    const w = s.matches ? 480 : 240;
    this.light.forEach(light => {
      if (!light.marker) { return; }
      const options = light.marker.getPopup()?.options || {};
      options.minWidth = options.maxWidth = w;
    });
  }

  ngOnDestroy(): void {
    this._subWidth?.unsubscribe();
    delete (window as any).atmosColShowArea;
    delete (window as any).atmosColToggleRow;
    delete (window as any).markCol;
    delete (window as any).nextCol;
  }

  scrollToList(): void {
    const table = this.table.nativeElement as HTMLElement;
    table.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  unlockAll(): void {
    if (!confirm('Are you sure you want to mark all Children of Light as found?')) { return; }
    this._dataService.wingedLightConfig.items.forEach(wl => {
      if (wl.unlocked) { return; }
      this.toggleWingedLight(wl, true);
      this.updateMarker(wl);
      this.updateTable(wl);
    });
    this.api?.refreshCells({ force: true });
  }

  reset(): void {
    if (!confirm('Are you sure you want to reset your found Children of Light?')) { return; }
    const wlGuids: Array<string> = [];
    this._dataService.wingedLightConfig.items.forEach(wl => {
      wl.unlocked = false;
      wlGuids.push(wl.guid);
    });
    this._storageService.removeWingedLights(...wlGuids);
    this.unlockedCol = 0;
    this.rows.forEach(r => r.unlocked = 0);
    this.light.forEach(l => { l.marker?.setOpacity(1); });
    this.map.closePopup();
    this.api?.refreshCells({ force: true });
    this._changeDetectorRef.markForCheck();
  }

  resetOrbit(): void {
    if (!confirm('Did you leave Orbit? This will lock everything but the winged light from Orbit.')) { return; }
    const wlGuids: Array<string> = [];
    this._dataService.wingedLightConfig.items.forEach(wl => {
      wl.unlocked = false;
      wlGuids.push(wl.guid);
    });
    this._storageService.removeWingedLights(...wlGuids);
    this.unlockedCol = 0;
    this.rows.forEach(r => r.unlocked = 0);
    this.light.forEach(l => { l.marker?.setOpacity(1); });
    this.map.closePopup();
    const orbitGuid = 'DvYGWvjeOC';
    const orbitWl = this.lightMap[orbitGuid]?.wl;
    if (orbitWl) {
      this.toggleWingedLight(orbitWl, true);
      this.updateMarker(orbitWl);
      this.updateTable(orbitWl);
    }
    this.api?.refreshCells({ force: true });
    this._changeDetectorRef.markForCheck();
  }

  showArea(row: IRow): void {
    this.shownArea = row.area;
    const lights = this.light.filter(v => v.wl.area === this.shownArea);
    const wl = lights.find(v => !v.wl.unlocked)?.wl ?? lights[0]?.wl;
    if (!wl) { return; }
    this.flyAndOpen(wl, true);
  }

  toggleRow(row: IRow, found?: boolean): void {
    found ??= row.unlocked < row.wl.length;
    this.map.closePopup();
    row.area.wingedLights?.forEach(wl => {
      this.toggleWingedLight(wl, found);
      this.updateTable(wl);
      this.updateMarker(wl);
    });
  }

  private createPopup(wl: IWingedLight): HTMLElement {
    const div = this._mapInstanceService.ttFlex();
    div.classList.add('s-leaflet-tooltip');
    div.setAttribute('data-wl', wl.guid);

    const grid = this._mapInstanceService.ttGrid();
    div.appendChild(grid);

    grid.appendChild(this._mapInstanceService.ttArea(wl.area));
    grid.appendChild(this._mapInstanceService.ttRealm(wl.area.realm));
    grid.insertAdjacentHTML('beforeend', `
<div class="container s-leaflet-item s-leaflet-desc">
  <div class="s-leaflet-menu-icon s-leaflet-maticon s-leaflet-maticon-desktop">description</div><div class="s-leaflet-menu-label">${wl.description || ''}</div>
</div>`);

    let videoUrl = wl.mapData?.videoUrl;
    if (videoUrl?.includes('youtube')) {
      div.insertAdjacentHTML('beforeend', `
<div>
  <iframe class="s-leaflet-vid" width="480" height="270" src="${videoUrl}" title="CoL" frameborder="0" allow="autoplay; encrypted-media;" allowfullscreen></iframe>
</div>`);
    } else if (videoUrl) {
      // A <video> instead of an iframe, as Firefox letterboxes iframed media documents in white.
      div.insertAdjacentHTML('beforeend', `
<div>
  <video class="s-leaflet-vid" width="480" height="270" src="/assets/game/col/${videoUrl}" title="CoL" controls autoplay playsinline></video>
</div>`);
    }

    const divButtons = document.createElement('div');
    div.appendChild(divButtons);
    divButtons.insertAdjacentHTML('beforeend', `
<button type="button" class="s-leaflet-hasicon s-leaflet-found" data-action="mark" onclick="markCol(this, '${wl.guid}', false)" title="Mark as found.">
  <span class="s-leaflet-maticon">check_box_outline_blank</span>
  <span>Found</span>
</button>`);
    divButtons.insertAdjacentHTML('beforeend', `
<button type="button" class="s-leaflet-hasicon" data-action="mark" onclick="markCol(this, '${wl.guid}', true)" title="Mark as found and go to next light.">
  <span class="s-leaflet-maticon">done_all</span>
</button>`);
    divButtons.insertAdjacentHTML('beforeend', `
<button class="s-leaflet-hasicon" data-direction="left" type="button" onclick="nextCol('${wl.guid}', -1)" title="Go to previous light.">
  <span class="s-leaflet-maticon">arrow_back</span>
</button>`);
    divButtons.insertAdjacentHTML('beforeend', `
<button class="s-leaflet-hasicon" data-direction="right" type="button" onclick="nextCol('${wl.guid}', 1)" title="Go to next light.">
  <span class="s-leaflet-maticon">arrow_forward</span>
</button>`);

    if (wl._wiki?.href) {
      divButtons.insertAdjacentHTML('beforeend', `<a href="${wl._wiki.href}" class="button link s-leaflet-right" target="_blank">Wiki</a>`);
    }

    return div;
  }

  private onPopupOpen(e: L.PopupEvent): void {
    const div = e.popup.getElement()?.querySelector('.s-leaflet-tooltip') as HTMLElement;
    if (!div) { return; }
    const wlGuid = div.dataset['wl'] as string;
    const wl = this._dataService.guidMap.get(wlGuid) as IWingedLight;
    this._lastPopup = e.popup;
    this.updatePopup(div, wl);
    this.fitPopupToMap(e.popup);
  }

  /** Shrinks the video so the whole popup fits within the map height. */
  private fitPopupToMap(popup: L.Popup): void {
    const el = popup.getElement();
    const video = el?.querySelector('.s-leaflet-vid') as HTMLElement;
    if (!el || !video) { return; }

    const marginBottom = parseInt(getComputedStyle(el).marginBottom, 10) || 0;
    const padding = L.point(popup.options.autoPanPadding || [5, 5]).y * 2;
    const overflow = el.offsetHeight + marginBottom + padding - this.map.getSize().y;
    if (overflow <= 0) { return; }

    video.style.height = `${Math.max(90, video.offsetHeight - overflow)}px`;
    // Re-setting the position is the public way to make Leaflet re-run autoPan.
    popup.setLatLng(popup.getLatLng()!);
  }

  private onMoveEnd(): void {
    const url = new URL(location.href);
    const c = this.map.getCenter();
    url.searchParams.set('x', `${Math.floor(c.lng)}`);
    url.searchParams.set('y', `${Math.floor(c.lat)}`);
    url.searchParams.set('z', `${this.map.getZoom()}`);
    window.history.replaceState(window.history.state, '', url.pathname + url.search);
  }

  private onKeydown(e: LeafletKeyboardEvent): void {
    if (!this._lastPopup?.isOpen()) { return; }
    const key = e.originalEvent.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== ' ') { return; }
    const el = this._lastPopup.getElement()?.querySelector('.s-leaflet-tooltip') as HTMLElement;
    const wlGuid = el.dataset['wl'] as string;
    if (!wlGuid) { return; }
    switch (key) {
      case 'ArrowLeft': this.prevCol(wlGuid); break;
      case 'ArrowRight': this.nextCol(wlGuid); break;
      case ' ': this.markCol(el.querySelector('[data-action="mark"]') as HTMLElement, wlGuid, false); break;
      default: return;
    }
    e.originalEvent.preventDefault();
    e.originalEvent.stopPropagation();
  }

  private updatePopup(div: HTMLElement, wl: IWingedLight): void {
    const foundIcon = div.querySelector('.s-leaflet-found .s-leaflet-maticon') as HTMLElement;
    foundIcon.innerText = wl.unlocked ? 'check_box' : 'check_box_outline_blank';
  }

  private updateMarker(wl: IWingedLight): void {
    const obj = this.lightMap[wl.guid];
    if (!obj) { return; }
    obj.marker?.setOpacity(wl.unlocked ? 0.4 : 1);
  }

  private updateTable(wl: IWingedLight): void {
    const row = this.rowMap[wl.guid];
    if (!row) { return; }
    row.unlocked = (row.wl || []).filter(w => w.unlocked).length;
    this.unlockedCol = this._storageService.getWingedLights().size;
    this._changeDetectorRef.markForCheck();
    this.api?.refreshCells({ force: true });
  }

  private flyAndOpen(wl: IWingedLight, scroll = false): void {
    if (!wl) { return; }
    const light = this.lightMap[wl.guid];
    if (!light) { return; }
    if (scroll) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    this.map.closePopup();
    const pos = wl.mapData?.position || [0, 0];
    light.marker?.openPopup();
    light.marker?.getElement()?.focus();
    setTimeout(() => {
      // The fly runs after the popup's autoPan, so pan again once it lands.
      this.map.once('moveend', () => {
        const popup = light.marker?.getPopup();
        if (popup?.isOpen()) { popup.setLatLng(popup.getLatLng()!); }
      });
      this.map.flyTo([pos[0] + 20, pos[1]], 3, { animate: true, duration: 0.5 });
    }, 1);
  }

  private toggleWingedLight(wl: IWingedLight, found?: boolean): void {
    wl.unlocked = typeof (found) === 'boolean' ? found : !wl.unlocked;
    if (wl.unlocked) {
      this._storageService.addWingedLights(wl.guid);
    } else {
      this._storageService.removeWingedLights(wl.guid);
    }
  }

  private allDone(): void {
    this.map.closePopup();
    this.map.flyTo([-270, 270], 0, { animate: false, duration: 0 });
  }

  private markCol(div: HTMLElement, guid: string, next: boolean): void {
    const wl = this._dataService.guidMap.get(guid) as IWingedLight;
    this.toggleWingedLight(wl, next ? true : undefined);
    this.updatePopup(div.parentElement as HTMLElement, wl);
    this.updateMarker(wl);
    this.updateTable(wl);
    if (next) { this.nextCol(guid, true); }
  }

  private prevCol(guid: string): void {
    const current = this.lightMap[guid];
    const prev = current?.prev;
    if (!prev?.wl) { return; }
    if (!prev.marker) { alert('Missing marker for previous Child of Light!'); return; }
    this.flyAndOpen(prev.wl);
  }

  private nextCol(guid: string, skipFound = false): void {
    const wl = this.lightMap[guid];
    if (!wl) { return; }
    let next = wl.next;
    if (!next?.wl) { return; }
    if (skipFound) {
      while (next && next !== wl) {
        if (next.wl && !next.wl.unlocked) { break; }
        next = next.next;
      }
      if (!next || next === wl) {
        this.allDone();
        return;
      }
    }
    if (!this.lightMap[next.wl.guid]?.marker) { alert('Missing marker for next Child of Light!'); return; }
    this.flyAndOpen(next.wl);
  }
}
